"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, usePathname } from "@/navigation";
import { useSearchParams } from "next/navigation";
import { Building2, Check, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usesVenueQueryParam } from "./venueScope";

interface SwitchableVenue {
  id: string;
  name: string;
}

interface Props {
  venues: SwitchableVenue[];
  activeVenueId: string | null;
}

// The venue is a scope, not a destination, so it gets its own control next to
// the logo instead of hiding inside the Reservations and Staff nav items. That
// keeps the nav items plain links and gives one place, at every width, to
// answer "which venue am I looking at".
//
// Desktop opens a dropdown under the trigger; mobile opens a bottom sheet,
// which is where a phone's thumb actually reaches.
const VenueSwitcher: React.FC<Props> = ({ venues, activeVenueId }) => {
  const t = useTranslations("Navigation");
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);

    // Only the dropdown needs click-outside, and its panel sits inside wrapRef.
    // The sheet is portalled out of that subtree and dismisses via its own
    // backdrop, so the same listener would close it on the mousedown that
    // starts a tap on one of its links.
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    if (isDesktop) document.addEventListener("mousedown", onClick);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, isDesktop]);

  // The sheet covers the page, so the page must not scroll underneath it.
  useEffect(() => {
    if (!open || isDesktop) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open, isDesktop]);

  // With a single venue there is nothing to switch between, and the page header
  // already names it.
  if (venues.length < 2) return null;

  const active = venues.find((v) => v.id === activeVenueId) ?? venues[0];

  // Switching keeps you where you are: /venues/a/staff -> /venues/b/staff, and
  // /reports?venueId=a -> /reports?venueId=b. Other filters in the query stay
  // put, so a deep link keeps everything but the venue.
  const hrefFor = (venueId: string) => {
    if (usesVenueQueryParam(pathname)) {
      const params = new URLSearchParams(searchParams.toString());
      params.set("venueId", venueId);
      return `${pathname}?${params.toString()}`;
    }
    // usePathname() may hand back a `pathnames` key rather than the live URL,
    // so the tail can still hold template segments. Only the venue id is ours
    // to fill; anything else left unfilled is not a URL we can build.
    const section = pathname.match(/^\/venues\/[^/]+\/(.+)$/)?.[1];
    return section && !section.includes("[")
      ? `/venues/${venueId}/${section}`
      : `/venues/${venueId}/reservations`;
  };

  const venueList = (rowPadding: string) => (
    <>
      <div className="px-4 py-2 text-[0.62rem] uppercase tracking-[0.12em] font-semibold text-gray-500">
        {t("Menu.SelectVenue")}
      </div>
      {venues.map((venue) => (
        <Link
          key={venue.id}
          href={hrefFor(venue.id) as any}
          onClick={() => setOpen(false)}
          className={`flex items-center justify-between gap-2 px-4 ${rowPadding} text-sm transition-colors hover:bg-gray-700/50 hover:text-white ${
            venue.id === active.id ? "text-white" : "text-gray-300"
          }`}
        >
          <span className="truncate">{venue.name}</span>
          {venue.id === active.id && (
            <Check className="w-4 h-4 shrink-0 text-blue-400" />
          )}
        </Link>
      ))}
    </>
  );

  // The nav bar carries backdrop-blur, which makes it the containing block for
  // fixed-position descendants. A sheet rendered inside it would resolve
  // bottom-0 against the 64px bar rather than the viewport, so it goes to the
  // body instead.
  const sheet = (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/60 z-[60]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-x-0 bottom-0 z-[70] max-h-[70vh] overflow-y-auto rounded-t-2xl border-t border-gray-700 bg-gray-800 py-1 shadow-xl"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            {/* Reads as "this pulls up from the bottom". */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="h-1 w-9 rounded-full bg-gray-600" />
            </div>
            {venueList("py-3")}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <div className="relative shrink-0" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t("Menu.SelectVenue")}
        aria-expanded={open}
        title={active.name}
        className="flex items-center gap-1.5 rounded-lg border border-gray-700/70 bg-gray-800/40 px-2 py-2 md:py-1.5 text-sm text-gray-200 transition-colors duration-200 hover:bg-gray-700/50 hover:text-white"
      >
        <Building2 className="w-4 h-4 shrink-0 text-gray-400" />
        {/* The name only earns its width once the nav row has room to spare. */}
        <span className="hidden xl:block max-w-[10rem] truncate">
          {active.name}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Absolute, so it resolves against this wrapper and stays put. */}
      <AnimatePresence>
        {open && isDesktop && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 mt-2 w-64 max-h-80 overflow-y-auto rounded-xl bg-gray-800 border border-gray-700 shadow-lg py-1 z-50"
          >
            {venueList("py-2")}
          </motion.div>
        )}
      </AnimatePresence>

      {mounted && !isDesktop && createPortal(sheet, document.body)}
    </div>
  );
};

export default VenueSwitcher;
